import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Receipt } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import TransactionItem from "../components/TransactionItem";
import { SkeletonRow, EmptyState } from "../components/Skeleton";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const FILTERS = [
    { value: "", label: "Semua" },
    { value: "success", label: "Sukses" },
    { value: "failed", label: "Gagal" },
    { value: "pending", label: "Menunggu" },
    { value: "processing", label: "Diproses" },
    { value: "refunded", label: "Dikembalikan" },
    { value: "cancelled", label: "Dibatalkan" },
];

export default function HistoryPage() {
    const navigate = useNavigate();
    const [txs, setTxs] = useState(null);
    const [filter, setFilter] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setTxs(null); setError("");
        api.get("/transactions", { params: { skip: page * 20, take: 20, ...(filter ? { status: filter.toUpperCase() } : {}) }, signal: controller.signal })
            .then(({ data }) => { setTxs(data.items || []); setTotal(data.total || 0); })
            .catch((err) => { if (!controller.signal.aborted) { setError(formatApiError(err)); setTxs([]); } });
        return () => controller.abort();
    }, [filter, page, revision]);

    const filtered = (txs || []).filter((tx) => {
        if (!search) return true;
        const name = tx.product?.name || tx.product_name || "";
        const target = tx.targetNumber || tx.customer_number || "";
        return (
            name.toLowerCase().includes(search.toLowerCase()) ||
            target.includes(search)
        );
    });

    return (
        <div className="space-y-4">
            <h1 className="font-heading font-bold text-xl tracking-tight">Riwayat Transaksi</h1>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    data-testid="history-search"
                    placeholder="Cari nomor / produk pada halaman ini..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                />
            </div>

            <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {FILTERS.map((f) => (
                    <Button
                        key={f.value}
                        data-testid={`filter-${f.value || "all"}`}
                        onClick={() => { setFilter(f.value); setPage(0); }}
                        size="sm"
                        variant={filter === f.value ? "default" : "outline"}
                        className={`rounded-full whitespace-nowrap ${filter === f.value ? "bg-brand-deep dark:bg-brand-green text-white dark:text-brand-deeper" : ""}`}
                    >
                        {f.label}
                    </Button>
                ))}
            </div>

            <div className="space-y-2">
                {error ? <div role="alert" className="text-sm text-destructive">{error}<Button variant="outline" className="ml-2" onClick={() => setRevision((value) => value + 1)}>Coba lagi</Button></div> : txs === null ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        title="Belum ada transaksi"
                        description={search ? "Coba kata kunci lain" : "Mulai transaksi untuk melihat riwayat di sini."}
                        icon={<Receipt className="h-7 w-7 text-muted-foreground" />}
                    />
                ) : (
                    filtered.map((tx, i) => (
                        <TransactionItem
                            key={tx.id}
                            tx={tx}
                            index={i}
                            onClick={() => navigate(`/riwayat/${tx.id}`)}
                        />
                    ))
                )}
            </div>
            <div className="flex items-center justify-between gap-2"><Button variant="outline" disabled={page === 0 || txs === null} onClick={() => setPage((value) => value - 1)}>Sebelumnya</Button><span className="text-xs text-muted-foreground">Halaman {page + 1} · {total} transaksi</span><Button variant="outline" disabled={(page + 1) * 20 >= total || txs === null} onClick={() => setPage((value) => value + 1)}>Berikutnya</Button></div>
        </div>
    );
}
